import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Collapse,
  Divider,
  Fade,
} from '@mui/material';
import { CheckCircle, Close } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { API_ENDPOINTS } from '../../config/api';
import {
  FEATURE_REQUEST_CATEGORIES,
  FeatureRequestListItem,
  SimilarFeatureRequest,
} from '../../types/featureRequests';
import VoteButton from './VoteButton';
import StatusPill from './StatusPill';
import SimilarityRing from './SimilarityRing';
import { colors } from '../../theme/theme';

interface SubmitFeatureRequestDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called when a brand new request is created. */
  onCreated: (created: FeatureRequestListItem) => void;
  /** Called when the studio votes one of the similar suggestions instead of creating a new one. */
  onVotedExisting: (id: string) => void;
}

const MIN_TITLE_LENGTH = 4;
const DEBOUNCE_MS = 400;

const SubmitFeatureRequestDialog: React.FC<SubmitFeatureRequestDialogProps> = ({
  open,
  onClose,
  onCreated,
  onVotedExisting,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [whyNeeded, setWhyNeeded] = useState('');
  const [category, setCategory] = useState('');
  const [showMoreFields, setShowMoreFields] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [similar, setSimilar] = useState<SimilarFeatureRequest[]>([]);
  const [similarLoading, setSimilarLoading] = useState(false);
  const [votedSuggestionId, setVotedSuggestionId] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reset = () => {
    setTitle('');
    setDescription('');
    setWhyNeeded('');
    setCategory('');
    setShowMoreFields(false);
    setError(null);
    setSimilar([]);
    setVotedSuggestionId(null);
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (title.trim().length < MIN_TITLE_LENGTH) {
      setSimilar([]);
      return;
    }
    setSimilarLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await apiService.get<SimilarFeatureRequest[]>(
          API_ENDPOINTS.FEATURE_REQUESTS.SIMILAR(title.trim()),
        );
        setSimilar(results);
      } catch {
        setSimilar([]);
      } finally {
        setSimilarLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]);

  const handleVoteSuggestion = async (id: string) => {
    try {
      // Pinned to the account's own studio under "All locations" (`__all__`
      // is refused for writes); a no-op everywhere else.
      await apiService.post(API_ENDPOINTS.FEATURE_REQUESTS.VOTE(id), undefined, apiService.atOwnStudio());
      setVotedSuggestionId(id);
      onVotedExisting(id);
    } catch {
      // Leave the suggestion as-is; the studio can try again.
    }
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim() || !whyNeeded.trim() || !category) {
      setError('Please fill in every field before submitting.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const created = await apiService.post<FeatureRequestListItem>(
        API_ENDPOINTS.FEATURE_REQUESTS.CREATE,
        { title: title.trim(), description: description.trim(), whyNeeded: whyNeeded.trim(), category },
        apiService.atOwnStudio(),
      );
      onCreated(created);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Could not submit your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: '22px', boxShadow: `0 24px 70px rgba(0,0,0,0.25), 0 8px 30px ${colors.primary}40` } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 800, fontSize: 19, pb: 1 }}>
        Submit a feature request
        <Box
          onClick={onClose}
          sx={{ width: 32, height: 32, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', '&:hover': { backgroundColor: colors.backgroundTertiary } }}
        >
          <Close sx={{ fontSize: 18, color: colors.greyText }} />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: colors.greyText3, letterSpacing: '0.4px', textTransform: 'uppercase', mb: 0.75, mt: 0.5 }}>
          Title
        </Typography>
        <TextField
          autoFocus
          fullWidth
          placeholder="What should we build?"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          inputProps={{ maxLength: 200 }}
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: '12px' },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
          }}
        />

        {title.trim().length >= MIN_TITLE_LENGTH && (
          <Box sx={{ mt: 2 }}>
            {similarLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Checking for similar requests…
                </Typography>
              </Box>
            )}

            {!similarLoading && similar.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Looks like these might be the same
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {similar.map((s) => (
                    <Box
                      key={s.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 1.4,
                        borderRadius: '13px',
                        border: `1px solid ${colors.divider}`,
                        backgroundColor: colors.backgroundTertiary,
                      }}
                    >
                      <VoteButton
                        voteCount={s.voteCount}
                        hasVoted={votedSuggestionId === s.id}
                        onToggle={() => handleVoteSuggestion(s.id)}
                        disabled={votedSuggestionId === s.id}
                        size={42}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                          {s.title}
                        </Typography>
                        <Box sx={{ mt: 0.4 }}>
                          <StatusPill status={s.status} size="small" />
                        </Box>
                      </Box>
                      <SimilarityRing score={s.similarityScore} />
                    </Box>
                  ))}
                </Box>

                {votedSuggestionId && (
                  <Fade in>
                    <Box
                      sx={{
                        mt: 1.5,
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: `${colors.success}14`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <CheckCircle sx={{ color: colors.success, fontSize: 20 }} />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        Thanks for voting! You'll get updates on that request.
                      </Typography>
                      <Button size="small" onClick={onClose}>
                        Close
                      </Button>
                    </Box>
                  </Fade>
                )}
              </Box>
            )}
          </Box>
        )}

        {!votedSuggestionId && title.trim().length >= MIN_TITLE_LENGTH && !showMoreFields && (
          <Box
            onClick={() => setShowMoreFields(true)}
            sx={{
              mt: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
              borderRadius: '12px',
              backgroundColor: colors.backgroundTertiary,
              padding: '12px',
              cursor: 'pointer',
              '&:hover': { backgroundColor: '#efeff4' },
            }}
          >
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: colors.greyText }}>
              {similar.length > 0 ? "None of these — continue with my request" : 'Continue'}
            </Typography>
          </Box>
        )}

        <Collapse in={showMoreFields && !votedSuggestionId}>
          <Divider sx={{ my: 2.5 }} />

          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: colors.greyText3, letterSpacing: '0.4px', textTransform: 'uppercase', mb: 1 }}>
            Category
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.875, mb: 2.5 }}>
            {FEATURE_REQUEST_CATEGORIES.map((c) => {
              const active = c === category;
              return (
                <Box
                  key={c}
                  onClick={() => setCategory(c)}
                  sx={{
                    padding: '7px 13px',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    backgroundColor: active ? colors.primary : colors.background,
                    border: `1.5px solid ${active ? colors.primary : colors.divider}`,
                    transition: 'border-color 150ms ease',
                    '&:hover': active ? undefined : { borderColor: colors.primary },
                  }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: active ? 700 : 600, color: active ? '#ffffff' : colors.greyText }}>
                    {c}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Description"
              placeholder="What should this feature do?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              inputProps={{ maxLength: 5000 }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: colors.backgroundTertiary } }}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Why do you need this?"
              placeholder="What problem does this solve for you or your members?"
              value={whyNeeded}
              onChange={(e) => setWhyNeeded(e.target.value)}
              inputProps={{ maxLength: 5000 }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: colors.backgroundTertiary } }}
            />
          </Box>
        </Collapse>

        {error && (
          <Typography variant="body2" sx={{ color: colors.error, mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        {showMoreFields && !votedSuggestionId && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            sx={{ borderRadius: '11px', boxShadow: `0 4px 14px ${colors.primary}66`, '&:hover': { boxShadow: `0 8px 22px ${colors.primary}80` } }}
          >
            {submitting ? <CircularProgress size={20} color="inherit" /> : 'Submit request'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SubmitFeatureRequestDialog;
